import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import type { AllRawData } from '../../raw-types.ts';

// Positional tuple — each field is a server/worker-read GameConfigData
// property. JSON dump drops object keys → about half the bytes vs a single
// `{blob: {...}}` wrapper. Add new fields by APPENDING to the list (legacy
// slots stay numbered; trailing nulls compress away on encode).
// Server-side gameConfig — every field is read by server or worker code.
// Client-side fields (navUnlockStages, leagueEndingSoonThreshold, etc.) live in
// the separate `clientGameConfig` R2 file via `buildClientGameConfig`.
//
// Tunable groups (wave_shop_config, pet_golden_summon_config, guest_config) are
// passed through as opaque jsonb — server code accesses them by named sub-key
// (`waveShopConfig.rerollCost`, `petGoldenSummonConfig.staticTriggers`, …).
export const GameConfigSchema = defineSchema({
  name: 'gameConfig',
  fields: [
    ['scoreConfig', { kind: 'json' }],
    ['stageTicketConfig', { kind: 'json' }],
    ['leagueTicketConfig', { kind: 'json' }],
    ['valhallaTicketConfig', { kind: 'json' }],
    ['degenTicketConfig', { kind: 'json' }],
    ['waveShopConfig', { kind: 'json' }],
    ['petGoldenSummonConfig', { kind: 'json' }],
    ['guestConfig', { kind: 'json' }],
    ['adTicketsDailyLimit', { kind: 'json' }],
    ['initialAssets', { kind: 'json' }],
    ['invites', { kind: 'json' }],
    ['pwaReward', { kind: 'json' }],
    ['valhallaSweepConfig', { kind: 'json' }],
    ['degenConfig', { kind: 'json' }],
    ['txTicketsDailyLimit', { kind: 'json' }],
    ['stageBonusConfig', { kind: 'json' }],
    ['statCaps', { kind: 'json' }],
    ['upgradeResetConfig', { kind: 'json' }],
    // ⚠ 슬롯 순서 고정: overdriveConfig 는 develop 에서 먼저 머지·시딩된 꼬리 슬롯이라
    // 그 인덱스를 유지해야 한다. 신규 필드는 반드시 그 뒤에 append.
    ['overdriveConfig', { kind: 'json' }],
    ['masterySpawnTiers', { kind: 'json' }],
    // ⚠ LEGACY(2026-08): 돌파석 지급이 stage_reward 컬럼으로 이관되어 이 슬롯은 더 이상
    // 읽히지 않는다. collectionConfig 슬롯 순서 보존을 위해 제거하지 않고 남긴다.
    ['breakthroughStoneDropConfig', { kind: 'json' }],
    // 컬렉션 v9(가중치·밴드·랭크 3테이블 병합) — Task 3 교훈(pet.breakthroughs 의
    // opaque json 회귀)과 달리, 여기서는 이 슬롯의 기존 관례 그대로 opaque json 유지:
    // 위 모든 config 블록이 동일하게 `{kind:'json'}` 이고(파일 상단 코멘트 "Tunable
    // groups... passed through as opaque jsonb"), collectionConfig 는 통째로 한
    // 객체(`{weights,bands,ranks}`)로만 소비되는 최상위 슬롯이라 pet.ts 처럼 배열
    // 원소를 포지셔널 서브스키마로 개별 디코드할 필요가 없다 — 포지셔널 gap-null
    // 함정(중간에 낀 optional 필드가 인코드 시 null 로 채워지는 문제)은 "배열 원소를
    // named positional sub-schema 로 표현할 때"만 발생하며, 이 필드는 원소 각각이
    // 아니라 전체를 하나의 JSON 값으로 통째 저장하므로 해당 없음.
    ['collectionConfig', { kind: 'json' }],
    // ⚠ append-only 꼬리 슬롯 — 절대 중간 삽입 금지.
    ['petBreakthroughConfig', { kind: 'json' }],
    // 결제 마일스톤 클레임(hotfix/payment-milestone-claim, Task 3) — V8 게임서버는
    // R2 외부 fetch 불가하므로 VX 가격표를 serverGameConfig 에 직접 시딩.
    // paymentEventConfig(v1) 은 payment-event-v2 Task 4 에서 은퇴 — 결제 이벤트는
    // 이제 독립 컬렉션 serverPaymentEvents/clientPaymentEvents(payment-event.ts) 로 시딩.
    // ⚠ productPriceMap 이 append-only 꼬리 슬롯 — 신규 필드는 반드시 그 뒤에 append.
    ['productPriceMap', { kind: 'json' }],
    // 스킨 강화 설정(skin-enhance v3 등급×레벨 공식 모델, game_config.skin_enhance_config).
    // 이 슬롯은 원래 skinDupeRewardConfig(중복 스킨→옷감 환산표)가 앉아 있던 자리다 —
    // 아직 어느 환경에도 배포되지 않은 trailing 슬롯이라 이름·의미만 교체했다(신규
    // 슬롯 추가 아님). `SkinDupeRewardConfig` 타입·`GameConfigData.skinDupeRewardConfig`
    // 필드는 소비처 0 확인 후 삭제됐다(Task 10) — delivery.ts 는 이미
    // `resolveSkinDupeShard`/`skinEnhanceConfig.dupeShardByGrade` 로 재배선 완료.
    // ⚠ append-only 꼬리 슬롯 — productPriceMap 뒤에 붙인다.
    ['skinEnhanceConfig', { kind: 'json' }],
    // 폴크방 입장권 리필(folkvangr.md §1.1) — ticket_configs.folkvangr 에서 온다.
    // ⚠ append-only 꼬리 슬롯 — skinEnhanceConfig 뒤에 붙인다.
    ['folkvangrTicketConfig', { kind: 'json' }],
  ],
});

function ticketCfg(
  ticketConfigs: Record<string, unknown> | null,
  mode: string
): { maxTickets: number; refillIntervalHours: number } | undefined {
  const entry = ticketConfigs?.[mode] as
    | { maxTickets?: number; refillIntervalHours?: number }
    | undefined;
  if (!entry) return undefined;
  return {
    maxTickets: Number(entry.maxTickets ?? 0),
    refillIntervalHours: Number(entry.refillIntervalHours ?? 0),
  };
}

/** collection_weight/collection_band/collection_rank 3테이블 → CollectionConfigData.
 *  전 테이블이 비어있으면 undefined(컬렉션 효과 전체 OFF, fail-closed). */
function buildCollectionConfig(
  raw: Pick<AllRawData, 'collection_weight' | 'collection_band' | 'collection_rank'>
): Record<string, unknown> | undefined {
  const weightRows = raw.collection_weight ?? [];
  const bandRows = raw.collection_band ?? [];
  const rankRows = raw.collection_rank ?? [];
  if (weightRows.length === 0 && bandRows.length === 0 && rankRows.length === 0) return undefined;

  return {
    weights: weightRows.map((r) => ({
      track: r.track,
      grade: Number(r.grade),
      ownPoint: Number(r.own_point),
      levelPoint: Number(r.level_point),
      breakthroughPoint: Number(r.breakthrough_point ?? 0),
    })),
    bands: bandRows.map((r) => ({
      track: r.track,
      from: Number(r.from_point),
      to: r.to_point == null ? null : Number(r.to_point),
      stepSize: Number(r.step_size),
      stepPct: Number(r.step_pct),
    })),
    ranks: rankRows.map((r) => ({
      rank: r.rank,
      requireTcp: Number(r.require_tcp),
      requirePcpMin: Number(r.require_pcp_min ?? 0),
      requireScpMin: Number(r.require_scp_min ?? 0),
      stats: (r.stats as Record<string, number> | null) ?? {},
      multipliers: (r.multipliers as Record<string, number> | null) ?? {},
    })),
  };
}

export function gameConfigFromRaw(
  row: Record<string, unknown>,
  raw?: Pick<AllRawData, 'collection_weight' | 'collection_band' | 'collection_rank'>
): Record<string, unknown> {
  const ticketConfigs = (row.ticket_configs ?? null) as Record<string, unknown> | null;
  const out: Record<string, unknown> = {};
  // Only set keys that have a real value — undefined slots become `null` on
  // encode/decode, so omitting them entirely keeps round-trip clean.
  const set = (key: string, value: unknown): void => {
    if (value !== undefined && value !== null) out[key] = value;
  };
  set('scoreConfig', row.score_config);
  set('stageTicketConfig', ticketCfg(ticketConfigs, 'stage'));
  set('leagueTicketConfig', ticketCfg(ticketConfigs, 'league'));
  set('valhallaTicketConfig', ticketCfg(ticketConfigs, 'valhalla'));
  set('degenTicketConfig', ticketCfg(ticketConfigs, 'degen'));
  set('waveShopConfig', row.wave_shop_config);
  set('petGoldenSummonConfig', row.pet_golden_summon_config);
  set('guestConfig', row.guest_config);
  set('adTicketsDailyLimit', row.ad_tickets_daily_limit);
  set('initialAssets', row.initial_assets);
  set('invites', row.invites);
  set('pwaReward', row.pwa_reward);
  set('valhallaSweepConfig', row.valhalla_sweep_config);
  set('degenConfig', row.degen_config);
  set('txTicketsDailyLimit', row.tx_tickets_daily_limit);
  set('stageBonusConfig', row.stage_bonus_config);
  set('statCaps', row.stat_caps);
  set('upgradeResetConfig', row.upgrade_reset_config);
  set('overdriveConfig', row.overdrive_config);
  set('masterySpawnTiers', row.mastery_spawn_tiers);
  set('breakthroughStoneDropConfig', row.breakthrough_stone_drop_config);
  if (raw) set('collectionConfig', buildCollectionConfig(raw));
  set('petBreakthroughConfig', row.pet_breakthrough_config);
  set('skinEnhanceConfig', row.skin_enhance_config);
  set('folkvangrTicketConfig', ticketCfg(ticketConfigs, 'folkvangr'));
  return out;
}

registerCategorySchema('gameConfig', GameConfigSchema);
